import { Builder, withChildren } from '@builder.io/react';
import image from '@/assets/images/cms/damImage.svg?inline';
import { DamFileIdentifier } from '@/contexts/DamFileProvider';

export interface DamImageComponentProps {
	readonly children?: React.ReactNode;
	readonly image?: DamFileIdentifier;
	readonly altText?: string;
	readonly imageFit?: string;
	readonly imagePosition?: { readonly x?: number; readonly y?: number };
	readonly aspectRatio?: { readonly desktop?: number; readonly tablet?: number; readonly mobile?: number };
	readonly attributes?: React.HTMLAttributes<HTMLDivElement>;
}

const register = <TProps extends DamImageComponentProps>(Component: React.FC<TProps>): void => {
	const DAMImageWithChildren = withChildren(Component);

	Builder.registerComponent(DAMImageWithChildren, {
		name: 'DAMImage',
		image,
		noWrap: true,
		canHaveChildren: true,
		inputs: [
			{
				name: 'image',
				type: 'Bizzkit DAM File',
				required: true,
			},
			{
				name: 'altText',
				type: 'string',
			},
			{
				name: 'imageFit',
				type: 'string',
				defaultValue: 'cover',
				enum: [
					{
						label: 'contain',
						value: 'contain',
						helperText: 'The image should never get cropped',
					},
					{
						label: 'cover',
						value: 'cover',
						helperText: 'The image should fill its container, cropping when needed',
					},
				],
			},
			{
				name: 'aspectRatio',
				type: 'object',
				subFields: [
					{
						name: 'desktop',
						type: 'number',
						helperText: `Height/width ratio, e.g. set to 1.5 for a 300px wide and 200px tall photo. Set to 0 for image
							to maintain its natural aspect ratio.`,
						defaultValue: 1.5,
						min: 0,
						max: 30,
					},
					{
						name: 'tablet',
						type: 'number',
						helperText: `Optional aspect ratio for tablet breakpoint. If unset, will use desktop size.`,
						min: 0,
						max: 30,
					},
					{
						name: 'mobile',
						type: 'number',
						helperText: `Optional aspect ratio for mobile breakpoint. If unset, will use tablet size, or desktop size if tablet
						size is unset.`,
						min: 0,
						max: 30,
					},
				],
			},

			{
				name: 'imagePosition',
				type: 'object',
				advanced: true,
				subFields: [
					{
						name: 'x',
						friendlyName: 'Horizontal',
						type: 'number',
						helperText: `Horizontal position of the image within its container when cropped, e.g. set to 50 for
									center, 0 for left, 100 for right`,
						defaultValue: 50,
						min: 0,
						max: 100,
					},
					{
						name: 'y',
						friendlyName: 'Vertical',
						type: 'number',
						helperText: `Vertical position of the image within its container when cropped, e.g. set to 50 for
									center, 0 for top, 100 for bottom`,
						defaultValue: 50,
						min: 0,
						max: 100,
					},
				],
			},
		],
	});
};

export default register;
